import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { LabelText, ValueText } from "../../pages/Properties/Details/styles";
import Typography from "../Typography";

interface DropdownProps {
  label?: string;
  onSearch?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeHolder?: string;
  name?: string;
  options: { label: string; value: string }[];
  value?: string;
  style?: {
    container?: React.CSSProperties;
    label?: React.CSSProperties;
    select?: React.CSSProperties;
    checkboxContainer?: React.CSSProperties;
    checkboxLabel?: React.CSSProperties;
    input?: React.CSSProperties;
  };
  enableSearch?: boolean;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  onSelect?: (string: string) => void;
  multiselect?: boolean;
  setSearch?: (value: string) => void;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  name,
  label,
  options,
  style,
  onChange,
  onSelect,
  enableSearch,
  placeHolder,
  onSearch,
  value: initialValue,
  setSearch,
  disabled,
}) => {
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [value, setValue] = useState<string>(initialValue || "");
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (onChange) {
      onChange(e);
    }
  };
  const handleSelect = (value: string) => {
    setValue(value);
    if (onSelect) {
      onSelect(value);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [dropdownVisible]);

  useEffect(() => {
    if (initialValue) setValue(initialValue);
  }, [initialValue]);

  if (enableSearch) {
    return (
      <div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            paddingTop: 14,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              marginBottom: 10,
            }}
          >
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: "500px",
                color: "#555A77",
              }}
            >
              {label}
            </Typography>
          </div>
        </div>
        <div
          ref={inputRef}
          style={{
            ...style?.input,
            border: "1px solid #F1EFFB",
            padding: 8,
            borderRadius: 6,
            alignItems: "center",
            justifyContent: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            backgroundColor: disabled ? "#f8f9fa" : "transparent"
          }}
          onClick={() => {
            if (disabled) return;
            setTimeout(() => {
              setDropdownVisible(!dropdownVisible);
            }, 300);
          }}
        >
          {!value && <ValueText>{placeHolder}</ValueText>}
          {value && <LabelText>{value}</LabelText>}
        </div>
        {dropdownVisible && (
          <div
            style={{
              position: "absolute",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: inputRef.current ? inputRef.current.offsetWidth : "100%",
              border: "1px solid #E0E0E0",
              background: "white",
              borderRadius: "5px",
              padding: "10px 10px",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              maxHeight: "300px",
              overflowY: "auto",
              gap: 10,
            }}
          >
            <div style={{ position: "sticky", display: "flex" }}>
              <input
                placeholder={"Search..."}
                type={name === "password" ? "password" : "text"}
                onChange={(e) => {
                  onSearch && onSearch(e);
                }}
                style={{
                  border: "1px solid #F1EFFB",
                  padding: 8,
                  borderRadius: 20,
                  width: "100%",
                }}
                name={name}
              />
            </div>

            {options.map((option, index) => (
              <div
                onMouseEnter={() => setIsHovered(option.value)}
                key={index}
                onClick={() => {
                  handleSelect(option.value);
                  setValue(option.label);
                  setDropdownVisible(false);
                  setSearch && setSearch(" ");
                }}
                onMouseLeave={() => setIsHovered(null)}
                style={{
                  padding: 5,
                  cursor: "pointer",
                  backgroundColor:
                    isHovered === option.value ? "#F1EFFB" : "white",
                }}
              >
                <ValueText>{option.label}</ValueText>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div style={style?.container}>
        {label && <LabelText style={style?.label}>{label}</LabelText>}

        <select
          name={name}
          disabled={disabled}
          style={{
            ...style?.select,
            width: "100%",
            height: 50,
            border: "1px solid #F1EFFB",
            borderRadius: 6,
            padding: "8px",
            background: "#F1EFFB",
            overflowY: "auto",
            maxHeight: "200px",
          }}
          onChange={handleSelectChange}
          value={value}
        >
          {options.length > 0 ? (
            options.map((option, index) => (
              <option
                style={{ fontFamily: "unset" }}
                key={index}
                value={option.value}
              >
                {option.label}
              </option>
            ))
          ) : (
            <LabelText>No options available</LabelText>
          )}
        </select>
      </div>
    );
  }
};

export default Dropdown;
